import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./BudgetIntakeTool.scss";
import { Page } from "../Components";
import { useSelector } from "react-redux";
import { currentCompanySelector } from "../redux/company";
import { useTabbedNav } from "../utils/hooks/useNav";
import { RouteComponentProps, Router } from "@reach/router";
import BudgetIntakeToolGoals from "./BudgetIntakeToolGoals";
import BudgetIntakeToolSettings from "./BudgetIntakeToolSettings";
import { awaitJSON, CrossChannelLambdaFetch, ToolsLambdaFetch } from "../utils/fetch-utils";
import { useSetError } from "../redux/modals";
import { emailSelector } from "../redux/user";
import * as R from "ramda";
import { processBulkImport } from "./excelUtils";
import { useExperimentFlag } from "../utils/experiments/experiment-utils";
import { BudgetIntakeRowInternal } from "@blisspointmedia/bpm-types/dist/BudgetIntakeTool";
import { LOCATIONS } from "../NavBar/locations";

interface GetBudgetIntakeRowsResponse {
  mappedBudgetEntries: any[];
}

interface GetCampaignTypesResponse {
  mediaTypes: string[];
  campaignLevelMediaTypes: string[];
}

interface GetBudgetCustomSegmentsList {
  segmentNames: string[];
}

const enum TabKey {
  NEW_GOALS = "new-goals",
  SETTINGS = "settings",
}

const NAVS = [
  { label: "New Goals", key: TabKey.NEW_GOALS },
  { label: "Settings", key: TabKey.SETTINGS },
];

const BudgetIntakeTool: React.FC = ({ navigate }: RouteComponentProps) => {
  const company = useSelector(currentCompanySelector);
  const email: string = useSelector(emailSelector);

  const [tableData, setTableData] = useState<BudgetIntakeRowInternal[]>();
  const [originalTableData, setOriginalTableData] = useState<BudgetIntakeRowInternal[]>();
  const [originalTableDataMap, setOriginalTableDataMap] = useState<Record<string, any>>();
  const [customSegmentsList, setCustomSegmentsList] = useState<string[]>();
  const [budgetIntakeSelectorOptionsPull, setBudgetIntakeSelectorOptions] = useState({});
  const [budgetIntakeHeadersCustom, setBudgetIntakeHeadersCustom] = useState<any[]>([]);
  const [showTableData, setShowTableData] = useState(false);
  const [deletedRows, setDeletedRows] = useState<BudgetIntakeRowInternal[]>([]);

  const [saving, setSaving] = useState(false);
  const [updatedRows, setUpdatedRows] = useState<BudgetIntakeRowInternal[]>([]);
  const [deletedBudget, setDeletedBudget] = useState<BudgetIntakeRowInternal[]>([]);
  const [newRows, setNewRows] = useState<BudgetIntakeRowInternal[]>([]);
  const [showPendingChanges, setShowPendingChanges] = useState(false);

  const [originalMediaTypesActive, setOriginalMediaTypesActive] = useState<Map<string, boolean>>(
    new Map()
  );
  const [mediaTypesActive, setMediaTypesActive] = useState<Map<string, boolean>>(new Map());
  const setError = useSetError();
  const shouldEnableBudgetIntakeTool = useExperimentFlag("enableBudgetIntakeTool");

  const enabledNavs = useMemo(() => {
    if (shouldEnableBudgetIntakeTool) {
      return NAVS;
    } else {
      return [];
    }
  }, [shouldEnableBudgetIntakeTool]);

  const { tab, goToTab } = useTabbedNav({
    navigate,
    baseURL: "budget-intake-tool",
    defaultKey: TabKey.NEW_GOALS,
    navs: NAVS.map(nav => nav.key, NAVS),
  });

  const getFreshBudgetIntakeData = useCallback(async () => {
    try {
      setShowTableData(false);

      let resCustom = await ToolsLambdaFetch("/getBudgetCustomMapping", {
        params: {
          company: company,
        },
      });
      let jsonResponseCustom = await awaitJSON<GetBudgetCustomSegmentsList>(resCustom);
      setCustomSegmentsList(jsonResponseCustom.segmentNames);

      let res = await ToolsLambdaFetch("/getBudgetIntakeData", {
        params: {
          company: company,
        },
      });
      let jsonResponse = await awaitJSON<GetBudgetIntakeRowsResponse>(res);
      setTableData(jsonResponse.mappedBudgetEntries);
      setOriginalTableData(jsonResponse.mappedBudgetEntries);

      let originalTableDataMap: Record<string, any> = {};
      for (let row of jsonResponse.mappedBudgetEntries) {
        originalTableDataMap[row.id] = row;
      }
      setOriginalTableDataMap(originalTableDataMap);
      setShowTableData(true);
      setDeletedRows([]);

      let resOptions = await ToolsLambdaFetch("/getBudgetIntakeLabelOptions", {
        params: {
          company: company,
        },
      });

      const sources = await CrossChannelLambdaFetch("/getSourceOfTruthOptions", {
        params: {
          company,
          ...LOCATIONS["Cross-Channel"].subPages.Overview.testCompanyParams,
        },
      });

      const sourceData = await awaitJSON(sources);

      const optionsByKey = {
        Source: sourceData,
      };

      let resultsOptions = await awaitJSON(resOptions);

      let goalNameOptions = [
        { label: "Budget", value: "Budget" },
        { label: "CPC", value: "CPC" },
        { label: "CPM", value: "CPM" },
        { label: "CPX", value: "CPX" },
        { label: "CTR", value: "CTR" },
        { label: "KPI Volume", value: "KPI Volume" },
        { label: "Revenue", value: "Revenue" },
        { label: "ROAS", value: "ROAS" },
      ];
      let updatedOptions = {
        ...resultsOptions,
        ...optionsByKey,
        goalNameOptions,
      };
      setBudgetIntakeSelectorOptions(updatedOptions);
    } catch (e) {
      setError({
        message: `Failed to get Budget Intake data. ERROR: ${
          e instanceof Error ? e.message : String(e)
        }`,
        reportError: e,
      });
    }
  }, [company, setError]);

  useEffect(() => {
    const dateHeaders = [
      {
        label: "Date Range",
        fieldStart: { label: "Start Date", field: "start_date" },
        fieldEnd: { label: "End Date", field: "end_date" },
        type: "dateRange",
        flex: 1,
        modalRow: 4,
        modalFlex: 1,
        required: true,
        bordered: true,
        customClass: "sameLineFieldLabel",
      },
    ];
    const goalHeaders = [
      {
        label: "Goal Type",
        field: "goal_name",
        type: "select",
        flex: 1,
        modalRow: 0,
        modalFlex: 1,
        options: "goalNameOptions",
        required: true,
      },
      {
        label: "Goal Value",
        field: "goal_value",
        type: "currency",
        flex: 1,
        modalRow: 1,
        modalFlex: 1,
        required: true,
      },
    ];
    const hiddenHeadersKpiAndSource = [
      {
        label: "KPI",
        field: "kpi",
        type: "select",
        flex: 1,
        modalRow: 2,
        modalFlex: 1,
        options: "Kpi",
        required: true,
      },
      {
        label: "KPI Source",
        field: "source",
        type: "select",
        flex: 1,
        modalRow: 3,
        modalFlex: 1,
        options: "Source",
        required: true,
      },
    ];
    const hiddenCampaign = [
      {
        label: "Campaign (Platform)",
        field: "campaign_platform",
        type: "select",
        isMulti: true,
        flex: 1,
        modalRow: customSegmentsList ? customSegmentsList.length + 5 : 1,
        modalFlex: 1,
        options: "Campaign (Platform)",
      },
    ];

    const segments = customSegmentsList || [];
    const segmentHeaders = segments.map((segment, index) => {
      let segmentHeader: {
        label: string;
        field: string;
        type: "select";
        isMulti: boolean;
        flex: 1;
        modalRow: number;
        modalFlex: 1;
        options: string;
        note?: string;
      } = {
        label: segment === "Channel" ? "Channel(s)" : segment,
        field: segment,
        type: "select",
        isMulti: segment === "Channel",
        flex: 1,
        modalRow: index + 5,
        modalFlex: 1,
        options: segment,
      };
      if (index === 0) {
        segmentHeader.note = "Select 1 or more of the following:";
      }
      return segmentHeader;
    });
    let combinedHeaders = [
      ...dateHeaders,
      ...segmentHeaders,
      ...goalHeaders,
      ...hiddenHeadersKpiAndSource,
      ...hiddenCampaign,
    ];
    setBudgetIntakeHeadersCustom(combinedHeaders);
  }, [
    setError,
    getFreshBudgetIntakeData,
    tableData,
    customSegmentsList,
    budgetIntakeSelectorOptionsPull,
  ]);

  useEffect(() => {
    if (!tableData) {
      (async () => {
        await getFreshBudgetIntakeData();
      })();
    }
  }, [setError, getFreshBudgetIntakeData, tableData]);

  const saveChanges = useCallback(async () => {
    const getInternalData = (displayData: any[]): any[] => {
      return displayData.map(row => {
        let segments = {};
        customSegmentsList?.forEach(segment => {
          segments[segment] = row[segment];
        });

        return {
          id: row.id,
          company: company,
          start_date: row.start_date,
          end_date: row.end_date,
          ...segments,
          goal_name: row.goal_name,
          goal_value: row.goal_value,
          lastuser: email,
          lastmodified: row.lastmodified,
          source: row.source,
          kpi: row.kpi,
          campaign_platform: row.campaign_platform,
        };
      });
    };

    let convertedNewRows = getInternalData(newRows || []);
    let convertedUpdatedRows = getInternalData(updatedRows || []);
    let convertedDeletedRows = getInternalData(deletedBudget || []);

    const internalBody: any = {
      insert: convertedNewRows,
      update: convertedUpdatedRows,
      delete: convertedDeletedRows,
      company: company,
      customSegments: customSegmentsList,
    };

    try {
      setSaving(true);
      await ToolsLambdaFetch<any>("/setBudgetIntakeData", {
        method: "POST",
        body: JSON.stringify(internalBody),
      });

      await getFreshBudgetIntakeData();
      setSaving(false);
    } catch (e) {
      setError({
        message: `Failed to set Budget Intake data. \n ERROR: ${
          e instanceof Error ? e.message : String(e)
        }`,
        reportError: e,
      });
    }
  }, [
    newRows,
    updatedRows,
    deletedBudget,
    company,
    customSegmentsList,
    email,
    getFreshBudgetIntakeData,
    setError,
  ]);

  useEffect(() => {
    if (tableData && originalTableDataMap && customSegmentsList) {
      let isSegmentUpdated = false;
      let isUpdated = (row: any) => {
        if (originalTableDataMap[row.id]) {
          let originalRow = originalTableDataMap[row.id];
          isSegmentUpdated = customSegmentsList.some(
            segment => row[segment] !== originalRow[segment]
          );

          return (
            row.id !== null &&
            (row.company !== originalRow.company ||
              row.start_date !== originalRow.start_date ||
              row.end_date !== originalRow.end_date ||
              isSegmentUpdated ||
              row.goal_name !== originalRow.goal_name ||
              row.goal_value !== originalRow.goal_value ||
              row.source !== originalRow.source ||
              row.campaign_platform !== originalRow.campaign_platform ||
              row.kpi !== originalRow.kpi)
          );
        }
        return false;
      };
      let updatedRows = R.filter(isUpdated, tableData);
      setUpdatedRows(updatedRows);

      let newNewRows = R.filter(row => {
        if (
          row.id ||
          row.start_date === null ||
          row.end_date === null ||
          customSegmentsList.every(segment => row[segment] === null) ||
          row.goal_name === null ||
          row.goal_value === null
        ) {
          return false;
        }
        return true;
      }, tableData);

      setNewRows(newNewRows);
    }
  }, [customSegmentsList, originalTableDataMap, tableData]);

  const hasPendingChanges: boolean = useMemo(() => {
    const hasPendingChangesResult =
      Boolean(newRows.length) || Boolean(updatedRows.length) || Boolean(deletedBudget.length);

    if (!hasPendingChangesResult) {
      setShowPendingChanges(false);
    }

    return hasPendingChangesResult;
  }, [newRows, deletedBudget, updatedRows]);

  const clearAllChanges = useCallback(() => {
    setTableData(originalTableData);
    setDeletedRows([]);
    setNewRows([]);
    setUpdatedRows([]);
  }, [originalTableData]);

  const bulkImport = useCallback(
    async file => {
      try {
        let res = await ToolsLambdaFetch("/getBudgetIntakeData", {
          params: {
            company: company,
          },
        });
        let jsonResponse = await awaitJSON<GetBudgetIntakeRowsResponse>(res);
        const importedChanges = processBulkImport(
          file,
          jsonResponse.mappedBudgetEntries,
          company,
          customSegmentsList || []
        );

        importedChanges.length > 0
          ? setTableData(importedChanges)
          : setError({
              message: "No updated rows or new changes",
            });
      } catch (e) {
        setError({
          message: `Failed to import changes ${e instanceof Error ? e.message : String(e)}`,
          reportError: e,
        });
      }
    },
    [company, customSegmentsList, setError]
  );

  const getCampaignTypesData = useCallback(async () => {
    try {
      let res = await ToolsLambdaFetch("/getCampaignTypes", {
        params: { company: company },
      });
      let resultsOptions = await awaitJSON<GetCampaignTypesResponse>(res);

      const campaignMap = new Map<string, boolean>(
        resultsOptions.mediaTypes.map(mediaType => [
          mediaType,
          resultsOptions.campaignLevelMediaTypes.includes(mediaType),
        ])
      );

      setMediaTypesActive(campaignMap);
      setOriginalMediaTypesActive(campaignMap);
    } catch (e) {
      setError({
        message: `Failed to get Campaigns Data. ERROR: ${
          e instanceof Error ? e.message : String(e)
        }`,
        reportError,
      });
    }
  }, [company, setError]);

  useEffect(() => {
    if (mediaTypesActive.size === 0) {
      (async () => {
        await getCampaignTypesData();
      })();
    }
  }, [setError, getCampaignTypesData, mediaTypesActive]);

  const handleMediaTypeActiveChange = (mediaType: string, value: string) => {
    setMediaTypesActive(prevMap => {
      const newMap = new Map(prevMap);
      newMap.set(mediaType, value === "campaign");
      return newMap;
    });
  };

  return (
    <Page
      app2Redesign
      title={
        <div className="budgetIntakeTitle">
          <div>Budget Intake Tool</div>
        </div>
      }
      pageType="Budget Intake Tool"
      navs={enabledNavs}
      selectedNav={tab}
      onNav={goToTab}
    >
      <Router className="fullPageRouter BudgetIntakeToolGoalsPage">
        <BudgetIntakeToolGoals
          company={company}
          tableData={tableData || []}
          setTableData={setTableData}
          originalTableData={originalTableData || []}
          originalTableDataMap={originalTableDataMap || {}}
          customSegmentsList={customSegmentsList || []}
          budgetIntakeHeadersCustom={budgetIntakeHeadersCustom || []}
          budgetIntakeSelectorOptionsPull={budgetIntakeSelectorOptionsPull}
          showTableData={showTableData}
          saving={saving}
          saveChanges={saveChanges}
          clearAllChanges={clearAllChanges}
          bulkImport={bulkImport}
          hasPendingChanges={hasPendingChanges}
          showPendingChanges={showPendingChanges}
          setShowPendingChanges={setShowPendingChanges}
          deletedBudget={deletedBudget}
          setDeletedBudget={setDeletedBudget}
          updatedRows={updatedRows}
          newRows={newRows}
          deletedRows={deletedRows}
          setDeletedRows={setDeletedRows}
          path={TabKey.NEW_GOALS}
        ></BudgetIntakeToolGoals>
        {mediaTypesActive && mediaTypesActive.size > 0 && (
          <BudgetIntakeToolSettings
            company={company}
            originalMediaTypesActive={originalMediaTypesActive}
            mediaTypesActive={mediaTypesActive}
            setMediaTypesActive={setMediaTypesActive}
            setOriginalMediaTypesActive={setOriginalMediaTypesActive}
            handleMediaTypeActiveChange={handleMediaTypeActiveChange}
            path={TabKey.SETTINGS}
          ></BudgetIntakeToolSettings>
        )}
      </Router>
    </Page>
  );
};

export default BudgetIntakeTool;
